/**
 * The words on a rename notice (plan step 2,
 * docs/plans/2026-09-24-dictionary-rename-downstream.md).
 *
 * The notice says what changed: each renamed field, old and new, with when and
 * how it happened and who did it — and, for a pairing that was inferred rather
 * than proven, says so ("The ledger is an audit record, so a guess must never
 * look like a fact"). A backstop line says a form's questions were replaced
 * and that other names may have changed without being identified.
 *
 * Pure - `web` has no test runner of its own; see renameNoticeText.test.ts.
 */

export interface NoticeRenameLike {
  ledgerId: string;
  oldName: string;
  newName: string;
  path: string;
  certainty?: string;
  formCode?: string | null;
  triggeredByEmail?: string | null;
  renamedAt?: unknown;
}

export interface NoticeBackstopLike {
  backstopId: string;
  formId?: string;
  formCode?: string | null;
  path: string;
  triggeredByEmail?: string | null;
  replacedQuestionCount?: number;
  identifiedLedgerIds?: string[];
  /** The renames it found, by name (kept on the backstop so the line survives their clearing). */
  identifiedRenames?: Array<{ oldName: string; newName: string; certainty?: string }>;
  at?: unknown;
}

export interface RenameNoticeLike {
  renames?: Record<string, NoticeRenameLike>;
  backstops?: Record<string, NoticeBackstopLike>;
}

export interface RenameNoticeText {
  headline: string | null;
  /** One line per old → new pair; ledgerIds are every record behind it. */
  renameLines: Array<{ ledgerId: string; ledgerIds: string[]; text: string }>;
  /** Distinct fields renamed (a repeated rename or a round trip is one field). */
  fieldCount: number;
  backstopLines: Array<{ backstopId: string; text: string }>;
  hasRenames: boolean;
  hasBackstops: boolean;
}

const PATH_WORDS: Record<string, string> = {
  adoption: 'dictionary adoption',
  importDd: 'Import DD',
  crfBuilder: 'CRF Builder edit',
  ddRegeneration: 'dictionary regeneration',
  packetReextraction: 'packet re-extraction',
  referenceFormImport: 'reference form import',
  reconcile: 'reconcile',
  roundTrip: 'dictionary round trip',
  unattributed: 'a save that did not say where it came from',
};
const pathWords = (p: string) => PATH_WORDS[p] ?? p;
const article = (w: string) => (/^[AEIOU]/i.test(w) ? 'an' : 'a');

/**
 * Distinct fields: names joined by a rename on the same form are one field,
 * so PULSE → VSPULSE twice, or WEIGHT → WTKG → WEIGHT, each count once.
 */
function countFields(renames: NoticeRenameLike[]): number {
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    const p = parent.get(k) ?? k;
    if (p === k) return k;
    const root = find(p);
    parent.set(k, root);
    return root;
  };
  const keys = new Set<string>();
  for (const r of renames) {
    const a = `${r.formCode ?? ''}|${r.oldName}`;
    const b = `${r.formCode ?? ''}|${r.newName}`;
    keys.add(a); keys.add(b);
    const ra = find(a); const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  }
  return new Set([...keys].map(find)).size;
}

/** Our own month names: en-GB's short September is "Sept" in current ICU data. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toMillis(v: unknown): number | null {
  const t = v as any;
  if (t == null) return null;
  if (typeof t === 'number') return Number.isFinite(t) ? t : null;
  if (t instanceof Date) return t.getTime();
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  return null;
}

export function formatWhen(v: unknown, timeZone?: string): string | null {
  const ms = toMillis(v);
  if (ms === null || Math.abs(ms) > 8.64e15) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone, day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${MONTHS[Number(get('month')) - 1]} ${get('hour')}:${get('minute')}`;
}

const byTime = <T,>(items: T[], at: (x: T) => unknown) =>
  [...items].sort((a, b) => (toMillis(at(a)) ?? 0) - (toMillis(at(b)) ?? 0));

export function renameNoticeText(notice: RenameNoticeLike | null | undefined, opts: { timeZone?: string } = {}): RenameNoticeText {
  const renames = byTime(Object.values(notice?.renames ?? {}), (r) => r.renamedAt);
  const backstops = byTime(Object.values(notice?.backstops ?? {}), (b) => b.at);
  const allRenames = notice?.renames ?? {};

  // The same rename recorded by two paths (a CRF Builder edit, then the
  // dictionary regeneration that followed) is one change: one line, naming both.
  const groups = new Map<string, NoticeRenameLike[]>();
  for (const r of renames) {
    const k = `${r.formCode ?? ''}|${r.oldName}→${r.newName}`;
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const renameLines = [...groups.values()].map((rs) => {
    const [first, ...rest] = rs;
    const when = formatWhen(first.renamedAt, opts.timeZone);
    const how = [pathWords(first.path), when, first.triggeredByEmail ? `by ${first.triggeredByEmail}` : null].filter(Boolean).join(', ');
    const also = rest.length
      ? `; also recorded by ${rest.map((r) => [pathWords(r.path), formatWhen(r.renamedAt, opts.timeZone)].filter(Boolean).join(', ')).join('; ')}`
      : '';
    // Inferred only when nothing proved it: one certain record makes it certain.
    const inferred = rs.some((r) => r.certainty === 'certain') ? '' : ' — inferred from matching question text, not proven';
    return { ledgerId: first.ledgerId, ledgerIds: rs.map((r) => r.ledgerId), text: `${first.oldName} → ${first.newName} (${how}${also})${inferred}` };
  });
  const fieldCount = countFields(renames);

  const backstopLines = backstops.map((b) => {
    const form = b.formCode || b.formId || 'a form';
    const when = formatWhen(b.at, opts.timeZone);
    // By name from the backstop itself when it carries them; otherwise from the
    // renames still on the notice.
    const named = b.identifiedRenames?.length
      ? b.identifiedRenames
      : (b.identifiedLedgerIds ?? []).map((id) => allRenames[id]).filter((r): r is NoticeRenameLike => !!r);
    const found = named.map((r) => `${r.oldName} → ${r.newName}${r.certainty === 'certain' ? '' : ', inferred'}`);
    const identified = found.length
      ? `${found.length} rename${found.length === 1 ? ' was' : 's were'} identified (${found.join('; ')}).`
      : (b.identifiedLedgerIds ?? []).length
        ? `${(b.identifiedLedgerIds ?? []).length} rename(s) were identified.`
        : 'No rename was identified.';
    return {
      backstopId: b.backstopId,
      text:
        `Form ${form}'s questions were replaced by ${article(pathWords(b.path))} ${pathWords(b.path)}${when ? ` on ${when}` : ''}` +
        `${b.triggeredByEmail ? ` (${b.triggeredByEmail})` : ''}. ${identified} ` +
        `Other names on this form may have changed without being identified. Regenerate to be sure.`,
    };
  });

  let headline: string | null = null;
  if (renameLines.length) {
    headline = `Built before ${fieldCount} field${fieldCount === 1 ? ' was' : 's were'} renamed. This document still uses the old name${fieldCount === 1 ? '' : 's'}.`;
  } else if (backstopLines.length) {
    headline = 'This document may use field names that have since changed.';
  }
  return { headline, renameLines, fieldCount, backstopLines, hasRenames: renameLines.length > 0, hasBackstops: backstopLines.length > 0 };
}

/**
 * Documents that can only be regenerated, and why — "Update field names only"
 * is not offered on them. Must match the server word for word
 * (functions/src/fieldRenames/renameDocuments.ts, kind "regenerateOnly");
 * renameDocuments.test.ts holds the two together.
 */
export const REGENERATE_ONLY_REASONS: Readonly<Record<string, string>> = {
  dataManagement__crfInstructionGuide:
    'The completion guide is stored as a finished Word file, so there is no text to update. Regenerate it instead.',
  dataManagement__acrf:
    'Every download of the annotated CRF rebuilds it, so a names-only change would be silently undone. Regenerate it instead.',
};

/**
 * The document key for a stored document path ("dataManagement/sdtmMapping" →
 * "dataManagement__sdtmMapping"). A full path ("projects/{id}/...") is accepted
 * and its study prefix dropped. The server computes the same key
 * (functions/src/fieldRenames/renameDocuments.ts, documentKeyOf).
 */
export const documentKeyOf = (path: string) => {
  const parts = path.split('/');
  return (parts[0] === 'projects' ? parts.slice(2) : parts).join('__');
};
